import {
  type EntitlementUsageDto,
  Permission,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { Label, Switch } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

import { isUnlimitedQuota } from '@/core/authz/entitlement-catalog';
import { Can } from '@/core/components/authz/can';
import {
  BillingCard,
  BillingCardTitle,
  BillingPlusBadge,
} from '@/core/components/billing/billing-card';
import {
  UnlimitedUsageRow,
  UsageBar,
} from '@/core/components/billing/usage-bar';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useDictionary } from '@/core/providers/dictionary-provider';

type BillingUsageCardProps = {
  plan: WorkspacePlan | undefined;
  seats: number | undefined;
  usage: EntitlementUsageDto | undefined;
  feedbackPaygEnabled: boolean;
  feedbackPaygPending?: boolean;
  onFeedbackPaygChange?: (enabled: boolean) => void;
  canManage: boolean;
};

export function BillingUsageCard({
  plan,
  seats,
  usage,
  feedbackPaygEnabled,
  feedbackPaygPending,
  onFeedbackPaygChange,
  canManage,
}: BillingUsageCardProps) {
  const dict = useDictionary();
  const overview = dict.workspace.billing.overview;
  const usageDict = dict.workspace.billing.usage;

  const seatUsage = usage?.members;
  const repUsage = usage?.reputationLocations;
  const feedbackUsage = usage?.feedbackInviteRecipients;
  const isFree = plan === WorkspacePlan.FREE || plan == null;

  const showFeedback =
    Boolean(feedbackUsage) &&
    plan !== WorkspacePlan.FREE &&
    !isUnlimitedQuota(feedbackUsage?.included ?? 0);

  const rows: ReactNode[] = [];

  if (seatUsage && seatUsage.limit > 0) {
    rows.push(
      <UsageBar
        key="seats"
        label={overview.seatsLabel}
        current={seatUsage.active}
        limit={seatUsage.limit}
        detail={(isFree
          ? // Free bills nothing; "1 abgerechnet" read as a charge (DEV-561).
            overview.seatsHintFree
          : plan === WorkspacePlan.PLUS
            ? overview.seatsHint
            : overview.seatsHintTeams
        )
          .replace('{used}', String(seatUsage.active))
          .replace('{billed}', String(seats ?? seatUsage.limit))}
      />,
    );
  } else if (seatUsage) {
    rows.push(
      <UnlimitedUsageRow
        key="seats-unlimited"
        label={overview.seatsLabel}
        detail={usageDict.seatsUnlimited.replace(
          '{current}',
          String(seatUsage.active),
        )}
      />,
    );
  }

  if (isFree) {
    rows.push(
      <div key="rep-free" className="border-t border-border/70 pt-4">
        <UsageBar
          label={overview.reputationLabel}
          current={0}
          limit={0}
          empty
          trailing={<BillingPlusBadge label={overview.plusBadge} />}
          detail={overview.reputationFreeHint}
        />
      </div>,
    );
  } else if (repUsage) {
    rows.push(
      <div key="rep" className="border-t border-border/70 pt-4">
        {isUnlimitedQuota(repUsage.included) ? (
          <UnlimitedUsageRow
            label={overview.reputationLabel}
            detail={usageDict.reputationUnlimited.replace(
              '{connected}',
              String(repUsage.connected),
            )}
          />
        ) : (
          <UsageBar
            label={overview.reputationLabel}
            current={repUsage.connected}
            limit={Math.max(repUsage.included, repUsage.connected, 1)}
            detail={overview.reputationDetail
              .replace('{connected}', String(repUsage.connected))
              .replace('{included}', String(repUsage.included))
              .replace('{billed}', String(repUsage.billed))}
          />
        )}
      </div>,
    );
  }

  if (showFeedback && feedbackUsage) {
    rows.push(
      <div key="feedback" className="border-t border-border/70 pt-4">
        <UsageBar
          label={usageDict.feedbackInvites}
          current={feedbackUsage.used}
          limit={Math.max(feedbackUsage.included, feedbackUsage.used, 1)}
          detail={usageDict.feedbackDetail
            .replace('{used}', String(feedbackUsage.used))
            .replace('{included}', String(feedbackUsage.included))
            .replace(
              '{payg}',
              feedbackPaygEnabled
                ? usageDict.feedbackPaygOn
                : usageDict.feedbackPaygOff,
            )}
        />
        {canManage && onFeedbackPaygChange ? (
          <div className="mt-3 flex min-h-11 items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-0.5">
              <Label htmlFor="feedback-payg">
                {usageDict.feedbackPaygTitle}
              </Label>
              <SettingsInfoHint label={usageDict.feedbackPaygTitle}>
                {usageDict.feedbackPaygDescription}
              </SettingsInfoHint>
            </div>
            <Can permission={Permission.BILLING_MANAGE}>
              <Switch
                id="feedback-payg"
                checked={feedbackPaygEnabled}
                disabled={feedbackPaygPending}
                onCheckedChange={onFeedbackPaygChange}
                aria-label={
                  feedbackPaygEnabled
                    ? usageDict.feedbackPaygDisable
                    : usageDict.feedbackPaygEnable
                }
              />
            </Can>
          </div>
        ) : null}
      </div>,
    );
  }

  if (rows.length === 0) return null;

  return (
    <BillingCard aria-label={overview.usageTitle}>
      <BillingCardTitle>{overview.usageTitle}</BillingCardTitle>
      <div className="flex flex-col gap-[18px]">{rows}</div>
    </BillingCard>
  );
}
